import React, { useState, useRef } from "react";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api, API_ROOT, extractErr } from "@/lib/api";
import { Upload, FileSpreadsheet, CheckCircle2, AlertCircle, FileText, ArrowRight } from "lucide-react";

export default function InventoryImport() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const onFile = async (f) => {
    if (!f) return;
    setFile(f);
    setPreview(null);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const token = localStorage.getItem("operon_token");
      const resp = await fetch(`${API_ROOT}/inventory-import/preview`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.detail || "Preview failed");
      setPreview(data);
    } catch (e) {
      toast.error(e.message || "Preview failed");
    }
  };

  const commit = async () => {
    if (!file) return;
    setCommitting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("operon_token");
      const resp = await fetch(`${API_ROOT}/inventory-import/commit`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.detail || "Commit failed");
      setResult(data);
      if (data.success > 0) toast.success(`Imported ${data.success} item${data.success === 1 ? "" : "s"}`);
      if (data.skipped > 0) toast.message(`Skipped ${data.skipped} duplicate SKU${data.skipped === 1 ? "" : "s"}`);
      if (data.total_errors > 0) toast.error(`${data.total_errors} row${data.total_errors === 1 ? "" : "s"} had errors`);
    } catch (e) { toast.error(e.message || "Commit failed"); }
    setCommitting(false);
  };

  const reset = () => { setFile(null); setPreview(null); setResult(null); if (inputRef.current) inputRef.current.value = ""; };

  return (
    <AppShell title="Inventory Import" subtitle="Upload your existing inventory from CSV or Excel — we'll handle the rest">
      {!result && (
        <div className="rounded-2xl border border-border bg-card p-5 mb-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center"><FileSpreadsheet className="w-5 h-5" /></div>
            <div>
              <h3 className="font-heading font-semibold">Import from CSV / Excel</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Required columns (any case): <span className="font-mono">name</span>, <span className="font-mono">sku</span>, <span className="font-mono">quantity</span>, <span className="font-mono">category</span>. Optional: <span className="font-mono">unit, barcode, supplier, min_stock, reorder_qty, unit_cost, notes</span>. Existing SKUs are skipped automatically.
              </p>
            </div>
          </div>
        </div>
      )}

      {!file && (
        <label
          htmlFor="import-file"
          className="block rounded-2xl border-2 border-dashed border-border bg-card p-10 text-center cursor-pointer hover:border-primary/50 hover:bg-accent/30 transition-colors"
          data-testid="import-dropzone"
        >
          <Upload className="w-10 h-10 mx-auto text-primary opacity-70" />
          <div className="mt-3 font-medium">Click to choose a CSV or Excel file</div>
          <div className="text-xs text-muted-foreground mt-1">.csv, .xlsx (.xls) — up to several thousand rows</div>
          <input id="import-file" ref={inputRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} data-testid="import-file-input" />
        </label>
      )}

      {file && preview && !result && (
        <div className="space-y-3">
          <div className="rounded-2xl border border-border bg-card p-4 flex items-center gap-3">
            <FileText className="w-5 h-5 text-primary" />
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{file.name}</div>
              <div className="text-xs text-muted-foreground">{preview.row_count} rows detected</div>
            </div>
            <Button variant="ghost" size="sm" onClick={reset}>Change file</Button>
          </div>

          {/* Mapping summary */}
          <div className="rounded-2xl border border-border bg-card p-4">
            <div className="font-medium mb-2 flex items-center gap-2">
              <ArrowRight className="w-4 h-4 text-primary" />
              Detected column mapping
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
              {Object.entries(preview.mapping || {}).map(([orig, canon]) => (
                <div key={orig} className="flex items-center gap-2 rounded-lg border border-border px-2 py-1">
                  <span className="font-mono text-xs flex-1 truncate">{orig}</span>
                  <ArrowRight className="w-3 h-3 text-muted-foreground" />
                  <span className={`font-mono text-xs ${preview.required.includes(canon) ? "text-emerald-500 font-semibold" : "text-muted-foreground"}`}>{canon}</span>
                </div>
              ))}
            </div>
            {preview.missing_required?.length > 0 && (
              <div className="mt-3 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                Missing required columns: <span className="font-mono">{preview.missing_required.join(", ")}</span>
              </div>
            )}
          </div>

          {/* Preview table */}
          <div className="rounded-2xl border border-border bg-card overflow-hidden">
            <div className="p-3 border-b border-border text-sm font-medium">First {Math.min(preview.preview?.length || 0, 20)} rows preview</div>
            <div className="overflow-x-auto max-h-[40vh]">
              <table className="w-full text-xs">
                <thead className="bg-muted/50 sticky top-0">
                  <tr>
                    {(preview.headers || []).map((h) => (
                      <th key={h} className="text-left font-medium px-2 py-1.5 whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(preview.preview || []).map((row, i) => (
                    <tr key={i} className="border-t border-border hover:bg-accent/20">
                      {(preview.headers || []).map((h) => (
                        <td key={h} className="px-2 py-1 whitespace-nowrap">{String(row[h] ?? "")}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={reset}>Cancel</Button>
            <Button onClick={commit} disabled={committing || (preview.missing_required || []).length > 0} data-testid="import-commit-button">
              <Upload className="w-4 h-4 mr-1.5" />{committing ? "Importing…" : `Import ${preview.row_count} rows`}
            </Button>
          </div>
        </div>
      )}

      {result && (
        <div className="rounded-2xl border border-border bg-card p-5 max-w-2xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-500/10 text-emerald-500 flex items-center justify-center"><CheckCircle2 className="w-5 h-5" /></div>
            <div>
              <div className="font-heading font-semibold">Import complete</div>
              <div className="text-sm text-muted-foreground">{file?.name}</div>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-3 gap-3">
            <div className="rounded-lg border border-border p-3 text-center"><div className="text-xs text-muted-foreground">Imported</div><div className="text-2xl font-mono text-emerald-500">{result.success}</div></div>
            <div className="rounded-lg border border-border p-3 text-center"><div className="text-xs text-muted-foreground">Skipped (duplicate SKU)</div><div className="text-2xl font-mono text-muted-foreground">{result.skipped}</div></div>
            <div className="rounded-lg border border-border p-3 text-center"><div className="text-xs text-muted-foreground">Errors</div><div className="text-2xl font-mono text-destructive">{result.total_errors}</div></div>
          </div>
          {result.errors?.length > 0 && (
            <div className="mt-4 rounded-lg border border-destructive/30 bg-destructive/5 p-3 max-h-[280px] overflow-y-auto">
              <div className="text-xs font-medium text-destructive mb-2">First {result.errors.length} errors</div>
              <div className="space-y-1 text-xs">
                {result.errors.map((er, i) => (
                  <div key={i} className="flex gap-2"><span className="font-mono text-muted-foreground">Row {er.row}:</span><span>{er.reason}</span></div>
                ))}
              </div>
            </div>
          )}
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" onClick={reset}>Import another file</Button>
            <Button onClick={() => window.location.href = "/app/inventory"} data-testid="import-view-inventory">View inventory</Button>
          </div>
        </div>
      )}
    </AppShell>
  );
}
